// src/pages/OpioidConverter.jsx

import React, { useState, useMemo, useEffect } from 'react';
import { Pill } from 'lucide-react';

const FormInput = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
    <input 
      {...props}
      className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" 
    />
  </div>
);

const FormSelect = ({ label, children, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
    <select
      {...props}
      className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
    >
      {children}
    </select>
  </div>
);

export default function OpioidConverter() {
  const opioids = useMemo(() => [
    { name: "Morphine", poFactor: 1.0, ivFactor: 3.0 },
    { name: "Oxycodone", poFactor: 1.5, ivFactor: 2.0 },
    { name: "Hydromorphone", poFactor: 4.0, ivFactor: 20.0 },
    { name: "Hydrocodone", poFactor: 1.0, ivFactor: null },
    { name: "Fentanyl Patch (mcg/hr)", poFactor: 2.4, ivFactor: null },
    { name: "Tramadol", poFactor: 0.1, ivFactor: null },
    { name: "Codeine", poFactor: 0.15, ivFactor: null },
  ], []);

  const [fromAmount, setFromAmount] = useState("10");
  const [fromOpioid, setFromOpioid] = useState(opioids[0]);
  const [fromRoute, setFromRoute] = useState('PO');
  const [toOpioid, setToOpioid] = useState(opioids[1]);
  const [toRoute, setToRoute] = useState('PO');

  useEffect(() => { if (!fromOpioid.ivFactor) setFromRoute('PO'); }, [fromOpioid]);
  useEffect(() => { if (!toOpioid.ivFactor) setToRoute('PO'); }, [toOpioid]);

  const toAmount = useMemo(() => {
    const fromFactor = fromRoute === 'IV' ? fromOpioid.ivFactor : fromOpioid.poFactor;
    const toFactor = toRoute === 'IV' ? toOpioid.ivFactor : toOpioid.poFactor;
    if (!fromFactor || !toFactor) return 0;
    const mme = parseFloat(fromAmount) * fromFactor;
    return mme / toFactor;
  }, [fromAmount, fromOpioid, fromRoute, toOpioid, toRoute]);

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2 mb-4"><Pill size={20}/> Opioid Converter</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4 p-4 bg-slate-100 dark:bg-zinc-800 rounded-lg">
          <h4 className="font-semibold text-center text-gray-700 dark:text-gray-300">From</h4>
          <FormInput label="Amount (mg)" value={fromAmount} onChange={(e) => setFromAmount(e.target.value)} type="number" />
          <FormSelect label="Opioid" value={fromOpioid.name} onChange={(e) => setFromOpioid(opioids.find(o => o.name === e.target.value))}>
            {opioids.map(o => <option key={o.name} value={o.name}>{o.name}</option>)}
          </FormSelect>
          <FormSelect label="Route" value={fromRoute} onChange={(e) => setFromRoute(e.target.value)} disabled={!fromOpioid.ivFactor}>
            <option value="PO">PO</option>
            {fromOpioid.ivFactor && <option value="IV">IV</option>}
          </FormSelect>
        </div>
        <div className="space-y-4 p-4 bg-slate-100 dark:bg-zinc-800 rounded-lg">
          <h4 className="font-semibold text-center text-gray-700 dark:text-gray-300">To</h4>
          <div className="text-center pt-5">
            <p className="text-4xl font-bold text-blue-600 dark:text-blue-400">{toAmount.toFixed(2)}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">mg ({toRoute}) of {toOpioid.name}</p>
          </div>
          <FormSelect label="Opioid" value={toOpioid.name} onChange={(e) => setToOpioid(opioids.find(o => o.name === e.target.value))}>
            {opioids.map(o => <option key={o.name} value={o.name}>{o.name}</option>)}
          </FormSelect>
          <FormSelect label="Route" value={toRoute} onChange={(e) => setToRoute(e.target.value)} disabled={!toOpioid.ivFactor}>
            <option value="PO">PO</option>
            {toOpioid.ivFactor && <option value="IV">IV</option>}
          </FormSelect>
        </div>
      </div>
    </div>
  );
}